import type { IconProps } from "@/v2-ui/icon"
// The tint recipe rides with the rule so every consumer of a product tone —
// the badge, a BadgeList pill — has the classes it names.
import "./licenseProduct.css"

/**
 * THE identity of a product in the backoffice: one hue and one glyph per
 * product (lic.Products), the same wherever its name is shown as a pill —
 * the Licenses board and panel, a customer's license chips, the add-on
 * catalog's "goes with", a license's product column — so an operator tells
 * Placepoint from Propcloud from the MCP servers at a glance, in any list.
 *
 * Keyed on the product name because every read that shows a product carries
 * its name and only some carry its code; the known products are assigned by
 * hand so the brand product is green and the AI family shares a cool range,
 * and a product this table has not met yet gets a stable tone from a hash
 * of its name, never grey, so a new product is recognisable from day one.
 * The hues are the data-viz palette the brand allows for categories; the
 * tint recipe is licenseProduct.css.
 */

const LICENSE_PRODUCT_TONES = [
  "green",
  "amber",
  "sky",
  "violet",
  "teal",
  "lime",
  "orange",
  "indigo",
  "fuchsia",
  "rose",
  "cyan",
  "slate",
] as const

type LicenseProductTone = (typeof LICENSE_PRODUCT_TONES)[number]

type LicenseProductIdentity = {
  tone: LicenseProductTone
  iconSrc: IconProps["src"]
}

// Known products by lowercase name (lic.Products.Name, en-GB). Placepoint,
// the core platform, takes the brand green; its MCP family shares the
// indigo–fuchsia–rose range so "an AI product" reads before the exact one.
const KNOWN_PRODUCTS: Record<string, LicenseProductIdentity> = {
  placepoint: { tone: "green", iconSrc: "map-marker" },
  propcloud: { tone: "amber", iconSrc: "house-building" },
  maps: { tone: "sky", iconSrc: "map" },
  valuepoint: { tone: "violet", iconSrc: "coins" },
  cloudpoint: { tone: "teal", iconSrc: "cloud" },
  kartdata: { tone: "lime", iconSrc: "layers" },
  "market data": { tone: "orange", iconSrc: "chart-line-up" },
  "placepoint mcp (norway)": { tone: "indigo", iconSrc: "sparkles" },
  "placepoint dataset mcp": { tone: "fuchsia", iconSrc: "database" },
  "placepoint mcp (sweden)": { tone: "rose", iconSrc: "sparkles" },
}

// Tones a product this table has not met yet is dealt from, by name hash.
// Keeps away from the brand green so an unknown product is never mistaken
// for Placepoint.
const FALLBACK_TONES: LicenseProductTone[] = [
  "cyan",
  "slate",
  "orange",
  "violet",
  "lime",
  "rose",
  "teal",
  "amber",
]

function hashName(name: string) {
  let hash = 0
  for (const char of name) {
    hash = (hash * 31 + char.charCodeAt(0)) | 0
  }
  return Math.abs(hash)
}

/**
 * The product's tone as badge classes plus its glyph — for pills rendered by
 * something other than LicenseProductBadge (a BadgeList item). Same identity,
 * same recipe (licenseProduct.css).
 */
export function getLicenseProductPillProps(
  productName: string | null | undefined,
): { className: string; iconSrc: IconProps["src"] } {
  const identity = getLicenseProductIdentity(productName)
  return {
    className: `license-product-badge license-product-badge-${identity.tone}`,
    iconSrc: identity.iconSrc,
  }
}

/** The hue and glyph for a product, by name; a stable fallback for one this file has not met. */
export function getLicenseProductIdentity(
  productName: string | null | undefined,
): LicenseProductIdentity {
  const name = (productName ?? "").trim().toLowerCase()
  const known = KNOWN_PRODUCTS[name]
  if (known) return known
  return {
    tone: FALLBACK_TONES[hashName(name) % FALLBACK_TONES.length],
    iconSrc: "box",
  }
}
